/**
 * For you today (opt-in): one meditation your AI picked from your history and
 * intentions - not your plan - with why it fits now. It stays until you begin
 * it, ask for something else, or leave it unopened a few days.
 * Turned off, it offers itself once (only to someone with an AI to use) and
 * then stays out of the way.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AiSettingsDto, FeaturedDto, MeditationDetailDto } from '@zenport/shared';
import { formatDuration } from '@zenport/shared';
import { api } from '../api.ts';
import { useApi } from '../hooks.ts';
import { usePrefs } from '../prefs.tsx';
import { usePlayer } from '../player/PlayerProvider.tsx';
import { itemLabel, TYPE_META } from '../content.ts';
import { Cover, Icon } from './ui.tsx';

const INVITE_KEY = 'zp-featured-invite-closed';
const inviteClosed = () => {
  try {
    return localStorage.getItem(INVITE_KEY) === '1';
  } catch {
    return false;
  }
};

export function FeaturedToday() {
  const { prefs, ready } = usePrefs();
  if (!ready) return null;
  return prefs.aiFeatured ? <Featured /> : <Invite />;
}

function Invite() {
  const { save } = usePrefs();
  const ai = useApi<AiSettingsDto>('/api/ai/settings');
  const [closed, setClosed] = useState(inviteClosed);
  const a = ai.data;
  const usable =
    !!a?.canUse && (a.configured || !a.sharedFeatures || a.sharedFeatures.includes('featured'));
  if (closed || !usable) return null;
  const close = () => {
    try {
      localStorage.setItem(INVITE_KEY, '1');
    } catch {
      /* a convenience only */
    }
    setClosed(true);
  };
  return (
    <section className="section" aria-labelledby="sec-feat">
      <div className="section-head">
        <h2 id="sec-feat">
          <Icon name="sparkle" size={16} /> For you today
        </h2>
      </div>
      <div className="feat-invite">
        <div className="feat-ghosts" aria-hidden="true">
          <span />
        </div>
        <div className="feat-invite-text">
          <strong>One meditation, picked for you</strong>
          <span className="sub">
            Your AI chooses from what you practise and why you practise - never your plan - and says
            why each fits today.
          </span>
        </div>
        <div className="feat-invite-actions">
          <button className="btn btn-sm btn-ghost" onClick={close}>
            Not now
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => void save({ aiFeatured: true })}
          >
            <Icon name="sparkle" size={14} /> Turn on
          </button>
        </div>
      </div>
    </section>
  );
}

function Featured() {
  const featured = useApi<FeaturedDto>('/api/ai/featured');
  const player = usePlayer();
  const [again, setAgain] = useState(false);
  const [starting, setStarting] = useState(false);
  const f = featured.data;
  const pick = f?.picks[0] ?? null;

  const pickAgain = async () => {
    setAgain(true);
    try {
      await api.post('/api/ai/featured/refresh');
    } finally {
      featured.reload();
      setAgain(false);
    }
  };

  const begin = async () => {
    if (!pick) return;
    setStarting(true);
    try {
      player.start(await api.get<MeditationDetailDto>(`/api/items/${pick.item.id}`));
    } finally {
      setStarting(false);
    }
  };

  if (f && !f.canUse && !pick) {
    return (
      <section className="section" aria-labelledby="sec-feat">
        <div className="section-head">
          <h2 id="sec-feat">
            <Icon name="sparkle" size={16} /> For you today
          </h2>
        </div>
        <p className="feat-quiet">
          Your AI is not connected any more. <Link to="/ai/setup?return=/">Set it up</Link> to get a
          pick for today.
        </p>
      </section>
    );
  }

  const loading = (featured.loading && !f) || again;
  return (
    <section className="section" aria-labelledby="sec-feat" aria-busy={loading || undefined}>
      <div className="section-head">
        <h2 id="sec-feat">
          <Icon name="sparkle" size={16} /> For you today
        </h2>
        {pick && (
          <button
            className="btn btn-sm btn-ghost feat-again"
            onClick={() => void pickAgain()}
            disabled={loading}
            title="Pick something else"
          >
            <Icon name="restart" size={14} /> Something else
          </button>
        )}
      </div>
      {loading ? (
        <div className="feat-one skeleton" aria-label="Your AI is choosing" />
      ) : pick ? (
        <article className="feat-one">
          <Link
            className="feat-one-cover"
            to={`/m/${pick.item.id}`}
            tabIndex={-1}
            aria-hidden="true"
          >
            <Cover coverId={pick.item.coverId} title={pick.item.title} size={640} />
          </Link>
          <div className="feat-one-body">
            <span className="feat-one-meta">
              {TYPE_META[pick.item.type].label}
              {pick.item.totalDurationSec ? ` · ${formatDuration(pick.item.totalDurationSec)}` : ''}
              {` · ${pick.item.creator}`}
            </span>
            <h3 className="feat-one-title">
              <Link to={`/m/${pick.item.id}`}>{itemLabel(pick.item)}</Link>
            </h3>
            {pick.why && <p className="feat-one-why">{pick.why}</p>}
            <div className="feat-one-actions">
              <button className="btn btn-primary" onClick={() => void begin()} disabled={starting}>
                <Icon name="play" size={16} /> Begin
              </button>
              <Link className="btn btn-ghost" to={`/m/${pick.item.id}`}>
                Details
              </Link>
            </div>
          </div>
        </article>
      ) : (
        <p className="feat-quiet">
          {f?.error ?? featured.error ?? 'Nothing picked yet.'}{' '}
          <button className="linkish" onClick={() => void pickAgain()}>
            Try again
          </button>
        </p>
      )}
    </section>
  );
}
